import { useState } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Globe, Trash, EnvelopeSimple, MapPin, Check, X } from "@phosphor-icons/react";

function ScoreBadge({ score, status }) {
  if (status !== "completed" || score === null || score === undefined) {
    return (
      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[--bg-muted] text-[--text-muted] inline-flex items-center">
        Pending
      </span>
    );
  }
  let bg, text;
  if (score >= 70) { bg = "var(--success-bg)"; text = "var(--success)"; }
  else if (score >= 40) { bg = "var(--warning-bg)"; text = "#B38F00"; }
  else { bg = "var(--danger-bg)"; text = "var(--danger)"; }

  return (
    <span
      className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center"
      style={{ background: bg, color: text }}
    >
      {score}/100
    </span>
  );
}

function SwipeableProspect({ prospect, isSelected, onSelect, onDelete, index }) {
  const x = useMotionValue(0);
  const bg = useTransform(x, [-120, -60, 0], ["var(--danger)", "var(--danger-bg)", "transparent"]);
  const trashOpacity = useTransform(x, [-100, -40, 0], [1, 0.6, 0]);
  const [removing, setRemoving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const handleDragEnd = (_, info) => {
    if (info.offset.x < -80) {
      setRemoving(true);
      setTimeout(() => onDelete(prospect.id), 200);
    }
  };

  const handleTrashClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setConfirming(true);
  };

  const handleConfirmDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setRemoving(true);
    setTimeout(() => onDelete(prospect.id, false), 200);
  };

  const handleBlockAndDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setRemoving(true);
    setTimeout(() => onDelete(prospect.id, true), 200);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setConfirming(false);
  };

  const p = prospect;

  return (
    <AnimatePresence>
      {!removing && (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0, padding: 0 }}
          transition={{ delay: index * 0.02, exit: { duration: 0.2 } }}
          className="relative overflow-hidden"
        >
          {/* Red delete background (for swipe) */}
          <motion.div
            className="absolute inset-0 flex items-center justify-end pr-6"
            style={{ backgroundColor: bg }}
          >
            <motion.div style={{ opacity: trashOpacity }}>
              <Trash size={20} className="text-white" weight="bold" />
            </motion.div>
          </motion.div>

          {/* Card content */}
          <motion.div
            drag="x"
            dragConstraints={{ left: -120, right: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            style={{ x }}
            onClick={() => { if (!confirming) onSelect(p); }}
            data-testid={`prospect-card-${p.id}`}
            className={`
              relative group cursor-pointer p-4 transition-colors bg-white
              ${isSelected
                ? "bg-[--primary-light] border-l-4 border-l-[--primary]"
                : "hover:bg-[--bg-hover] border-l-4 border-l-transparent"
              }
            `}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-[--text-primary] truncate">
                    {p.name}
                  </h3>
                  <ScoreBadge score={p.audit_score} status={p.audit_status} />
                </div>
                {p.website && (
                  <p className="text-xs text-[--text-muted] mt-1 truncate flex items-center gap-1">
                    <Globe size={12} /> {p.website}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-1.5">
                  {(p.zip_code || p.city) && (
                    <span className="text-[10px] text-[--text-muted] flex items-center gap-1">
                      <MapPin size={10} /> {p.city || p.zip_code}{p.city && p.zip_code ? `, ${p.zip_code}` : ""}
                    </span>
                  )}
                  {p.business_type && p.business_type !== "chiropractor" && (
                    <span className="text-[10px] text-[--text-secondary] bg-[--bg-muted] px-1.5 py-0.5 font-medium capitalize">
                      {p.business_type}
                    </span>
                  )}
                  {(p.email_status === "generated" || p.email_status === "sent") && (
                    <span className="text-[10px] text-[--primary] flex items-center gap-1 font-medium">
                      <EnvelopeSimple size={10} /> {p.email_status === "sent" ? "Sent" : "Email ready"}
                    </span>
                  )}
                  {p.email_status === "previously_contacted" && (
                    <span className="text-[10px] text-[#B38F00] bg-[--warning-bg] px-1.5 py-0.5 font-medium">
                      Previously contacted
                    </span>
                  )}
                </div>
              </div>

              {/* Trash / Confirm inline */}
              <div className="flex-shrink-0" onPointerDown={(e) => e.stopPropagation()}>
                {confirming ? (
                  <div className="flex items-center gap-1 animate-fade-in">
                    <button
                      data-testid={`confirm-delete-${p.id}`}
                      onPointerDown={handleConfirmDelete}
                      className="px-2 py-1 text-[10px] font-medium bg-[--bg-muted] text-[--text-secondary] hover:bg-[--danger-bg] hover:text-[--danger] transition-colors"
                      title="Delete this prospect"
                    >
                      Delete
                    </button>
                    <button
                      data-testid={`block-domain-${p.id}`}
                      onPointerDown={handleBlockAndDelete}
                      className="px-2 py-1 text-[10px] font-medium bg-[--danger] text-white hover:bg-red-700 transition-colors"
                      title="Delete and block this domain from future searches"
                    >
                      Block Site
                    </button>
                    <button
                      data-testid={`cancel-delete-${p.id}`}
                      onPointerDown={handleCancelDelete}
                      className="p-1 text-[--text-muted] hover:text-[--text-primary] transition-colors"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid={`delete-prospect-${p.id}`}
                    onPointerDown={handleTrashClick}
                    className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-[--danger-bg] hover:text-[--danger] transition-all"
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ProspectList({ prospects, selected, onSelect, onDelete }) {
  if (prospects.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8" data-testid="empty-prospect-list">
        <div className="text-center">
          <Globe size={36} className="mx-auto mb-3 text-[--text-muted]" />
          <p className="text-sm text-[--text-muted]">No prospects yet</p>
          <p className="text-xs text-[--text-muted] mt-1">Use the sidebar to discover businesses</p>
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[--border]" data-testid="prospect-list">
      {prospects.map((p, i) => (
        <SwipeableProspect
          key={p.id}
          prospect={p}
          isSelected={selected?.id === p.id}
          onSelect={onSelect}
          onDelete={onDelete}
          index={i}
        />
      ))}
    </div>
  );
}
